import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { facilitiesMapAtom } from "atoms/map/payload/facilities";
import { wellsMapAtom } from "atoms/map/payload/wells";
import axios, { CancelTokenSource } from "axios";
import { EMPTY_FILTER_ID, SERIES_LIMIT_EXCEEDED } from "constants/app.constants";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { setIsFiltering } from "store/features";
import type { IMapRequest } from "stores/map";
import { useMapStore } from "stores/map";

import { EMPTY_MAP_RESULT, getTxnId as fetchMapData } from "api/map";

export function useWellMapUpdater() {
  const dispatch = useDispatch();
  const setHasToConfirmSize = useMapStore((s) => s.setHasToConfirmSize);
  const setTxnId = useMapStore((s) => s.setTxnId);

  const facilitiesLegends = useAtomValue(facilitiesMapAtom);
  const setWellsMap = useSetAtom(wellsMapAtom);
  const jotaiStore = useStore();

  // Store the current cancel token source for request cancellation
  const cancelTokenSourceRef = useRef<Maybe<CancelTokenSource>>(null);

  /**
   * fetches well map data and updates atoms/Redux state
   * @param request - The well map request
   * @param screenshot - Whether this is for screenshot mode (skips atom updates)
   */
  const fetchWellMap = async (
    request: IMapRequest,
    screenshot = false,
    { skipTxnIdDispatch = false } = {}
  ) => {
    // Cancel any pending request before starting a new one
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel();
    }

    // A request that settles without a new legend still answers for its filter, so the
    // legend stops holding its rows for it (useIsLegendBehindFilter).
    const settleLegend = () => {
      if (!screenshot)
        setWellsMap({ ...jotaiStore.get(wellsMapAtom), filterId: request?.FilterId });
    };

    // Check if well filter is empty or invalid - return EMPTY_MAP_RESULT if so
    if (!request?.FilterId || request.FilterId === EMPTY_FILTER_ID) {
      settleLegend();
      return EMPTY_MAP_RESULT;
    }

    // Create new cancel token for this request
    // eslint-disable-next-line import/no-named-as-default-member
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    try {
      dispatch(setIsFiltering(true));
      const result = await fetchMapData(request, cancelTokenSource.token);
      dispatch(setIsFiltering(false));

      // Clear the cancel token ref after successful completion
      cancelTokenSourceRef.current = null;

      // getTxnId returns null on a non-cancel axios failure (5xx, network drop,
      // gateway timeout) instead of throwing -- without this guard, `result.error`
      // below throws a TypeError that the outer catch swallows into an empty map
      // with no toast and no Sentry event.
      if (!result) {
        setHasToConfirmSize(false);
        Sentry.withScope((scope) => {
          scope.setContext("fetchWellMap", { filterId: request.FilterId });
          scope.setLevel("error");
          Sentry.captureException(new Error("fetchWellMap: getTxnId returned null"));
        });
        toast.error("Couldn't load the map", { toastId: "fetch-well-map-error" });
        settleLegend();
        return EMPTY_MAP_RESULT;
      }

      // Handle series limit exceeded error. Only re-arms the confirm dialog on a
      // first (non-override) request: once OverrideSizeLimit is set, eva-data-svc-rs's
      // ceiling is bypassed entirely (EVA-33), so this response is no longer the
      // expected confirm-and-retry case -- it means the retry hit the ceiling anyway
      // (e.g. a deploy-order or header mismatch between the confirmed services). That
      // must not fall through silently: MapSeriesSizeConfirmation.onConfirm hides the
      // dialog unconditionally right after this call resolves, so treating it as the
      // normal case here would close the dialog with no error shown at all.
      if (result.error === SERIES_LIMIT_EXCEEDED && !request.OverrideSizeLimit) {
        setHasToConfirmSize(true);
        settleLegend();
        // result is guaranteed non-null by the `if (!result)` guard above.
        return result;
      } else {
        setHasToConfirmSize(false);
        // A non-empty, non-SERIES_LIMIT_EXCEEDED error is map-svc's generic catch
        // ("An error occurred - " + e.Message), which can include raw
        // RpcException/Status text (internal hostnames, ports) -- that must not
        // reach the user as-is, so it's reported to Sentry (same shape as
        // api/map.ts's fetchGroupBounds) with a generic toast instead. toastId
        // collapses repeats to one toast: fetchWellMap re-runs on every
        // filter/settings change.
        if (result.error) {
          Sentry.withScope((scope) => {
            scope.setContext("fetchWellMap", { filterId: request.FilterId });
            scope.setLevel("error");
            Sentry.captureException(new Error(result.error));
          });
          toast.error("Couldn't load the map", { toastId: "fetch-well-map-error" });
        }
      }

      if (!screenshot) {
        const dispatchPayload = {
          ...(result ?? EMPTY_MAP_RESULT),
          filterId: request?.FilterId,
          bBox: result?.bBox ?? { sw: [], ne: [] },
          facilityLegend: facilitiesLegends?.legend ?? {
            legendItems: [],
            title: ""
          },
          facilityId: facilitiesLegends?.id ?? ""
        };

        if (!skipTxnIdDispatch) {
          setTxnId(dispatchPayload);
        }

        setWellsMap({
          id: result?.id ?? "",
          legend: result?.legend ?? { legendItems: [], title: "" },
          filterId: request?.FilterId
        });
      }

      return result;
    } catch (err) {
      // Only clear the filtering state for real errors, not cancellations.
      // When a newer request cancels this one, it is already running and will
      // clear isFiltering when it completes — stopping the spinner here would
      // cause a visible flicker before the newer request finishes.
      if (!axios.isCancel(err)) {
        dispatch(setIsFiltering(false));
        settleLegend();
      }

      // Check if this error is due to cancellation
      if (axios.isCancel(err)) {
        return EMPTY_MAP_RESULT;
      }

      return EMPTY_MAP_RESULT;
    }
  };

  const cancelWellRequest = () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel();
      cancelTokenSourceRef.current = null;
    }
  };

  return {
    fetchWellMap,
    cancelWellRequest
  };
}
